import { useEffect, type RefObject } from "react";
import { mountRevisionDifference } from "./revision-difference-popover";

export type ReadOnlyRevisionDiff = {
  fields: readonly { path: string; previousValue: string; currentValue: string; state: "CHANGED" | "REMOVED" }[];
  additions: readonly { tablePath: string; text: string }[];
};

export function useReadOnlyRevisionDiff(
  formRef: RefObject<HTMLFormElement | null>,
  readOnly: boolean,
  revisionDiff: ReadOnlyRevisionDiff | null,
) {
  useEffect(() => {
    const root = formRef.current;
    if (!readOnly || !root || !revisionDiff) return;
    const cleanups: (() => void)[] = [];
    const changes = new Map(revisionDiff.fields.map((item) => [item.path, item]));
    root.querySelectorAll<HTMLElement>("[data-shift-diff-path]").forEach((element) => {
      const path = element.dataset.shiftDiffPath;
      const change = path ? changes.get(path) : undefined;
      if (!change) return;
      element.dataset.shiftRevisionDiff = change.state;
      const host = element.matches("tr")
        ? element.querySelector<HTMLElement>("td")
        : element.closest<HTMLElement>("td") ?? element;
      if (host) cleanups.push(mountRevisionDifference(root, host, {
        previousValue: change.previousValue,
        currentValue: change.currentValue,
        removed: change.state === "REMOVED",
      }));
    });
    const additions = new Map<string, string[]>();
    for (const addition of revisionDiff.additions) {
      const texts = additions.get(addition.tablePath) ?? [];
      texts.push(addition.text);
      additions.set(addition.tablePath, texts);
    }
    additions.forEach((texts, tablePath) => {
      const section = root.querySelector<HTMLElement>(`[data-shift-diff-path="${tablePath}"]`);
      if (!section) return;
      cleanups.push(mountRevisionDifference(root, section, {
        previousValue: "Bu sürümde yok",
        currentValue: texts.join("\n\n"),
        removed: false,
      }));
    });
    return () => {
      root.querySelectorAll<HTMLElement>("[data-shift-revision-diff]").forEach((element) => {
        delete element.dataset.shiftRevisionDiff;
      });
      cleanups.forEach((cleanup) => cleanup());
    };
  }, [formRef, readOnly, revisionDiff]);
}
